import React, { useCallback } from 'react';
import TableController, { type TableControllerProps } from './TableController';
import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import { ReactEditor, useSlateStatic } from 'slate-react';
import styles from './Table.module.css';
import classnames from 'classnames';
import type TableSelection from '../TableSelection';
import AhaEditorV2Controller from '../../../controllers/AhaEditorV2Controller';
import leftButtonPressed from '../leftButtonPressed';
import './Table.css';
import {
  type ComponentProps,
  type TableElement,
} from '../../../types/aha-editor';

const TableComponent: React.FC<TableControllerProps> = ({
  attributes,
  element,
  children,
  path,
}) => {
  const columnWidths: number[] = element.table_columnWidths || [];
  const tableWidth = element.table_width || '100%';
  const controller = useController(TableController);
  const cellsSelected =
    controller.state.tableSelection.tableHasSelectedCells(path);
  const hasHeaderRow = controller.state.tableElement.table_headerRow;

  const onMouseUp = (event: React.MouseEvent) => {
    controller.actionSetIsSelecting(false);
    controller.clickedAnchorCell = null;
  };

  const onMouseOver = (event: React.MouseEvent) => {
    if (leftButtonPressed(event) && controller.clickedAnchorCell) {
      controller.actionUpdateSelection(event.nativeEvent);
    }
  };

  const onMouseLeave = (event: React.MouseEvent) => {
    if (!leftButtonPressed(event) || !controller.clickedAnchorCell) return;

    const mouseOverDocument = (e: MouseEvent) => {
      controller.actionUpdateSelection(e);
      document.removeEventListener('mouseover', mouseOverDocument);
    };
    document.addEventListener('mouseover', mouseOverDocument);
  };

  // Split children for thead/tbody when hasHeaderRow is true
  const childrenArray = React.Children.toArray(children);
  const headerRow = hasHeaderRow ? childrenArray[0] : null;
  const bodyRows = hasHeaderRow ? childrenArray.slice(1) : childrenArray;

  return (
    <div
      className="table-wrapper"
      style={{ width: tableWidth, maxWidth: '100%' }}
    >
      <table
        {...attributes}
        className={classnames('Editor__table', {
          [styles.tableCellsSelected]: cellsSelected,
          'Editor__table--sticky-header': hasHeaderRow,
        })}
        onMouseUp={onMouseUp}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
        style={{
          width: tableWidth,
          maxWidth: '100%',
        }}
      >
        <colgroup>
          {columnWidths.map((width, index) => (
            <col key={index} style={{ width: `${width}%` }} />
          ))}
        </colgroup>
        {hasHeaderRow && headerRow && (
          <thead className="Editor__thead">{headerRow}</thead>
        )}
        <tbody className="Editor__tbody">{bodyRows}</tbody>
      </table>
    </div>
  );
};

const ControlledTableComponent = StartControllerScope(
  TableController,
  ApplicationView(TableComponent)
);

// Include the editor in the controller props.
const WrappedTableComponent: React.FC<ComponentProps<TableElement>> = ({
  attributes,
  element,
  children,
  ...props
}) => {
  const editor = useSlateStatic();
  const path = ReactEditor.findPath(editor, element);
  const editorController = useController(AhaEditorV2Controller);

  const setTableSelection = useCallback(
    (tableSelection: TableSelection) => {
      editorController.actionSetPluginState('tableSelection', tableSelection);
    },
    [editorController]
  );

  return (
    <ControlledTableComponent
      editor={editor}
      attributes={attributes}
      element={element}
      path={path}
      tableSelection={
        editorController.state.pluginState.tableSelection as TableSelection
      }
      setTableSelection={setTableSelection}
      {...props}
    >
      {children}
    </ControlledTableComponent>
  );
};

export default ApplicationView(WrappedTableComponent);
